import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListViewerWorkspaces } from '../hooks/useListWorkspaces';
import { Card, Spinner, Alert, Button, TextInput, Label } from 'flowbite-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import * as sched from 'sched';
import { useAuth } from '../hooks/useAuth';
import { FiPlus } from 'react-icons/fi';

interface Workspace {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface WorkspacesResponse {
  workspaces: Workspace[];
}

// Define validation schema for workspace creation form
const workspaceSchema = z.object({
  name: z.string().min(1, "Workspace name is required")
});

// Type for form values
type WorkspaceFormValues = {
  name: string;
};

const Workspaces: React.FC = () => {
  const navigate = useNavigate();
  const { workspaces, loading, error } = useListViewerWorkspaces();
  const { token, client } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle form submission to create a new workspace
  const handleCreateWorkspace = async (values: WorkspaceFormValues, { resetForm }: any) => {
    if (!token || !client) return;

    try {
      setIsSubmitting(true);

      // Create the workspace request
      const request: sched.CreateWorkspaceRequest = {
        name: values.name
      };

      // Call the API to create the workspace
      await sched.WrappedApiClient.create_workspace(client, token, request);

      // Reset form and hide it
      resetForm();
      setShowForm(false);

      // Refresh the workspaces list by refetching
      window.location.reload();
    } catch (error) {
      console.error('Error creating workspace:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
  };

  return (
    <div className="min-h-screen bg-cm-bg-deep p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-mono font-bold text-cm-text-primary">Your Workspaces</h1>
          <button
            onClick={toggleForm}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cm-accent-orange text-cm-bg-deep font-mono text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <FiPlus className="w-4 h-4" />
            {showForm ? 'Cancel' : 'New Workspace'}
          </button>
        </div>

        {/* Inline form for creating a new workspace */}
        {showForm && (
          <div className="mb-6 p-4 bg-cm-bg-surface rounded-xl border border-cm-border-subtle">
            <Formik
              initialValues={{ name: '' }}
              validationSchema={toFormikValidationSchema(workspaceSchema)}
              onSubmit={handleCreateWorkspace}
            >
              {({ isValid, dirty }) => (
                <Form className="flex flex-col gap-4">
                  <div>
                    <div className="mb-1">
                      <Label htmlFor="name">Workspace Name</Label>
                    </div>
                    <Field
                      as={TextInput}
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter workspace name"
                    />
                    <ErrorMessage name="name" component="div" className="text-red-400 text-xs mt-1" />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      color="blue"
                      size="sm"
                      disabled={!isValid || !dirty || isSubmitting}
                    >
                      {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
                      Create Workspace
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center p-8 text-cm-text-muted">
            <Spinner size="xl" aria-label="Loading workspaces" />
            <span className="ml-3">Loading your workspaces...</span>
          </div>
        ) : error ? (
          <Alert color="failure">
            <h3 className="text-lg font-semibold">Error loading workspaces</h3>
            <p>{typeof error === 'object' && error !== null && 'message' in error ? error.message : 'Unknown error'}</p>
          </Alert>
        ) : !workspaces || !workspaces.workspaces || workspaces.workspaces.length === 0 ? (
          <Alert color="info">
            <p>No workspaces available.</p>
          </Alert>
        ) : (
          <div className="divide-y divide-cm-border-subtle">
            {(workspaces as WorkspacesResponse).workspaces.map((workspace: Workspace) => (
              <div
                key={workspace.id}
                onClick={() => navigate(`/workspaces/${workspace.id}`)}
                className="cursor-pointer p-4 hover:bg-cm-bg-hover rounded-lg transition-colors"
              >
                <div className="flex flex-col">
                  <span className="text-lg font-medium text-cm-text-primary">
                    {workspace.name}
                  </span>
                  <span className="text-sm text-cm-text-muted font-mono">
                    ID: {workspace.id}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Workspaces;
